import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { Product, QuoteResult, User } from "../lib/api";

export type CartItem = {
  id: string;
  name: string;
  price: number;
  quantity: number;
  source: "quote" | "product";
  detail?: string;
  image?: string;
  stlFile?: string;
  stlColor?: string;
  stlColorHex?: string;
  quote?: QuoteResult;
  model_file_id?: number | null;
  stl_file?: string | null;
  stl_filename?: string;
  weight_grams?: number;
  volume_cm3?: number;
  quote_options?: Record<string, unknown>;
};

type Toast = {
  id: string;
  message: string;
  type?: "success" | "error" | "info" | "warning" | "cart-reminder" | "cart-added";
};

type Theme = "light" | "dark";

type AppStateContextValue = {
  user: User | null;
  isAdmin: boolean;
  cart: CartItem[];
  pendingFiles: File[];
  theme: Theme;
  toasts: Toast[];
  setUser: (user: User | null) => void;
  logout: () => void;
  addToast: (message: string, type?: Toast["type"]) => void;
  dismissToast: (id: string) => void;
  toggleTheme: () => void;
  addQuoteToCart: (quote: QuoteResult) => void;
  addProductToCart: (product: Product, quantity?: number, detail?: string, stlColor?: string, stlColorHex?: string) => void;
  updateCartQuantity: (id: string, quantity: number) => void;
  removeCartItem: (id: string) => void;
  clearCart: () => void;
  setPendingFiles: (files: File[]) => void;
};

const SESSION_KEY = "pd_session";
const CART_KEY = "pd_cart_react";
const THEME_KEY = "pd_theme";

const AppStateContext = createContext<AppStateContextValue | null>(null);

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function normalizePrice(value: unknown): number {
  const price = Number(value || 0);
  return Number.isFinite(price) ? price : 0;
}

export function AppStateProvider({ children }: { children: React.ReactNode }) {
  const [user, setUserState] = useState<User | null>(() => readJson<User | null>(SESSION_KEY, null));
  const [cart, setCart] = useState<CartItem[]>(() => readJson<CartItem[]>(CART_KEY, []));
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [theme, setTheme] = useState<Theme>(() => readJson<Theme>(THEME_KEY, "dark"));
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.classList.toggle("dark", theme === "dark");
    document.documentElement.style.colorScheme = theme;
    localStorage.setItem(THEME_KEY, JSON.stringify(theme));
  }, [theme]);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  const setUser = useCallback((nextUser: User | null) => {
    setUserState(nextUser);
    if (nextUser) localStorage.setItem(SESSION_KEY, JSON.stringify(nextUser));
    else localStorage.removeItem(SESSION_KEY);
  }, []);

  const logout = useCallback(() => setUser(null), [setUser]);

  const addToast = useCallback((message: string, type: Toast["type"] = "info") => {
    const id = crypto.randomUUID();
    setToasts((current) => {
      if (type === "cart-reminder" && current.some((toast) => toast.type === "cart-reminder")) {
        return current;
      }
      return [...current, { id, message, type }];
    });
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const addQuoteToCart = useCallback(
    (quote: QuoteResult) => {
      setCart((current) => [
        ...current,
        {
          id: crypto.randomUUID(),
          name: quote.filename,
          price: normalizePrice(quote.price),
          quantity: 1,
          source: "quote",
          detail: [
            quote.material ? `Material: ${quote.material}` : "",
            quote.color ? `Color: ${quote.color}` : ""
          ].filter(Boolean).join(" · "),
          quote,
          model_file_id: quote.model_file_id,
          stl_file: quote.stl_file,
          stlColor: quote.color,
          stl_filename: quote.filename,
          weight_grams: quote.weight,
          volume_cm3: quote.volume,
          quote_options: quote.quote_options
        }
      ]);
    },
    []
  );

  const addProductToCart = useCallback(
    (product: Product, quantity = 1, detail = "Standard Product", stlColor?: string, stlColorHex?: string) => {
      setCart((current) => {
        const colorKey = (stlColor || "default").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-");
        const itemId = `product-${product.id}-${colorKey}`;
        const existing = current.find((item) => item.source === "product" && item.id === itemId);
        if (existing) {
          return current.map((item) =>
            item.id === existing.id
              ? { ...item, quantity: item.quantity + quantity, detail, stlColor, stlColorHex }
              : item
          );
        }
        return [
          ...current,
          {
            id: itemId,
            name: product.name,
            price: normalizePrice(product.price),
            quantity,
            source: "product",
            detail,
            image: product.image,
            stlFile: product.stl_file,
            stlColor,
            stlColorHex
          }
        ];
      });
      addToast("Product added to cart", "cart-added");
    },
    [addToast]
  );

  const updateCartQuantity = useCallback((id: string, quantity: number) => {
    setCart((current) =>
      current.map((item) => (item.id === id ? { ...item, quantity: Math.max(1, quantity) } : item))
    );
  }, []);

  const removeCartItem = useCallback((id: string) => {
    setCart((current) => current.filter((item) => item.id !== id));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const value = useMemo<AppStateContextValue>(
    () => ({
      user,
      isAdmin: user?.role === "admin",
      cart,
      pendingFiles,
      theme,
      toasts,
      setUser,
      logout,
      addToast,
      dismissToast,
      toggleTheme: () => setTheme((current) => (current === "light" ? "dark" : "light")),
      addQuoteToCart,
      addProductToCart,
      updateCartQuantity,
      removeCartItem,
      clearCart,
      setPendingFiles
    }),
    [
      addProductToCart,
      addQuoteToCart,
      addToast,
      cart,
      dismissToast,
      logout,
      pendingFiles,
      removeCartItem,
      setUser,
      theme,
      toasts,
      updateCartQuantity,
      user
    ]
  );

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}

export function useAppState() {
  const value = useContext(AppStateContext);
  if (!value) throw new Error("useAppState must be used inside AppStateProvider");
  return value;
}
